/*
 * WillerTime · Cart hooks (Blade)
 *
 * 1. window.WT.addToCart(variantId, quantity, opts) — AJAX add that opens Pipeline's
 *    cart drawer and refreshes the header count. Any element with
 *    data-wt-atc="<variant id>" becomes a one-click add-to-cart button:
 *
 *      <button type="button" data-wt-atc="123456" data-wt-atc-qty="1"
 *              data-wt-discount="CODE">Get This Bundle</button>
 *
 *    data-wt-discount (optional) applies a Shopify discount code to the cart
 *    session before the item is added, so it shows up at checkout automatically.
 *
 * 2. Klaviyo auto-open guard — after any add-to-cart (ours or Pipeline's own),
 *    any Klaviyo Customer Hub panel / popup form that opens on its own within
 *    the next few seconds is closed again. The proper fix lives in Klaviyo
 *    (Customer Hub behaviour settings or a form with an "Added to cart" trigger);
 *    this is a belt-and-braces safeguard controlled by data-wt-chat-guard on <body>.
 *
 * 3. Cart drawer guard — whenever the cart drawer is open, what it shows is compared
 *    with the real cart (/cart.js). If they disagree (empty state while the cart has
 *    items, a missing line, a wrong quantity) the drawer is told to re-render. This
 *    catches any ordering problem between our code, Pipeline and cart apps.
 *    Turn it off with data-wt-cart-guard="false" on <body>.
 *
 * 4. Klaviyo ghost-popup fix — when a Klaviyo popup is closed it plays a fade-out and is
 *    supposed to unmount. When that never finishes (backgrounded tab, iOS Safari, the
 *    guard above closing it mid-animation) the popup stays as an invisible full-screen
 *    layer (z-index 90000) that swallows every tap: Add to Cart and Check Out look dead.
 *    Any Klaviyo dialog that is fading out is made click-through immediately and hidden
 *    once the fade should be over. While the cart drawer is open the Klaviyo teaser tab is
 *    hidden, and the Klaviyo Customer Hub / Chat sheet that Klaviyo opens by itself after an
 *    add-to-cart is closed with its own close button (it dimmed the page, locked scrolling
 *    and pushed the cart's Check Out block up the screen). The cart drawer is sized with
 *    100dvh so its Check Out block stays on the real bottom edge on iPhone/Android.
 *
 * 5. Tap rescue for Check Out / Add to Cart — the last line of defence, independent of
 *    what caused the problem:
 *    a) If a tap lands on the Check Out or Add to Cart button's area but is caught by an
 *       invisible layer on top of it (any element whose effective opacity is ~0 or that
 *       is hidden, e.g. a half-closed Klaviyo popup), the tap is passed to the button.
 *    b) After a Check Out tap, if the browser is still on the same page 3.5 seconds
 *       later (something swallowed the form submit), the shopper is sent to /checkout.
 *    Turn it off with data-wt-tap-rescue="false" on <body>.
 */
(function () {
  'use strict';

  var WT = window.WT = window.WT || {};
  var routes = (window.theme && window.theme.routes) || {};
  var cartRoute = routes.cart || '/cart';
  var root = (routes.root_url || '/').replace(/\/$/, '');

  /* ------------------------------------------------------------------ */
  /* Discount pre-apply                                                 */
  /* ------------------------------------------------------------------ */
  var appliedCodes = {};

  WT.applyDiscount = function (code) {
    code = (code || '').trim();
    if (!code) return Promise.resolve();
    if (appliedCodes[code]) return appliedCodes[code];
    // Hitting /discount/CODE sets the discount cookie for this session.
    // We follow the redirect but ignore the response body.
    appliedCodes[code] = fetch(root + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(cartRoute + '.js'), {
      credentials: 'same-origin',
      redirect: 'follow',
      cache: 'no-store'
    }).then(function () {}).catch(function () {});
    return appliedCodes[code];
  };

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                        */
  /* ------------------------------------------------------------------ */
  /*
   * Show the cart AFTER the fresh cart JSON has been broadcast.
   * Pipeline's drawer only re-renders when its "stale" flag is set, and that flag is
   * set by theme:cart:change. Opening the drawer before that event made it render the
   * old cart (empty state, or a list missing the item that was just added).
   */
  function showCart() {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (drawer) {
      if (drawer.classList.contains('drawer--visible')) {
        // Already open: ask Pipeline to re-render the line items in place
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      } else {
        drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
      }
      return true;
    }
    // No drawer on this page (cart page, or cart style set to "page")
    if (document.querySelector('[data-ajax-disable="true"]')) {
      window.location.reload();
    } else {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    }
    return false;
  }

  function refreshCart() {
    return fetch(cartRoute + '.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
        return cart;
      });
  }

  WT.addToCart = function (variantId, quantity, opts) {
    opts = opts || {};
    quantity = parseInt(quantity, 10) || 1;
    var body = new URLSearchParams({ id: String(variantId), quantity: String(quantity) }).toString();

    WT.lastAddAt = Date.now();
    document.dispatchEvent(new CustomEvent('wt:cart:adding', { bubbles: true, detail: { id: variantId } }));

    var pre = opts.discount ? WT.applyDiscount(opts.discount) : Promise.resolve();

    return pre.then(function () {
      return fetch(cartRoute + '/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: body
      });
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) { throw new Error(data.description || data.message || 'Could not add to cart'); }
        return data;
      });
    }).then(function (item) {
      WT.lastAddAt = Date.now();
      // 1) fetch the fresh cart and fire theme:cart:change (header count, entry counter,
      //    and Pipeline's drawer state all update), 2) THEN show the cart.
      return refreshCart()
        .catch(function (err) {
          if (window.console) console.warn('[WT] cart refresh failed:', err);
          return null;
        })
        .then(function () {
          if (!opts.silent) showCart();
          return item;
        });
    });
  };

  /* Delegated click handler for [data-wt-atc] */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wt-atc]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading') || btn.disabled) return;

    var id = btn.getAttribute('data-wt-atc');
    var qty = btn.getAttribute('data-wt-atc-qty') || 1;
    var discount = btn.getAttribute('data-wt-discount') || '';
    var label = btn.textContent;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = btn.getAttribute('data-wt-atc-adding') || 'Adding…';

    WT.addToCart(id, qty, { discount: discount }).then(function () {
      btn.textContent = btn.getAttribute('data-wt-atc-added') || 'Added ✓';
      setTimeout(function () { btn.textContent = label; }, 1800);
    }).catch(function (err) {
      btn.textContent = 'Try again';
      setTimeout(function () { btn.textContent = label; }, 2400);
      if (window.console) console.warn('[WT] add to cart failed:', err);
    }).then(function () {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    });
  });

  /* ------------------------------------------------------------------ */
  /* Cart drawer guard                                                   */
  /* ------------------------------------------------------------------ */
  (function () {
    var SETTLE_MS = 700;    // let Pipeline finish its own render before checking
    var VERIFY_MS = 1500;   // re-check after a repair
    var BUSY_MS = 500;      // retry delay while Pipeline shows its loading state
    var MAX_REPAIRS = 2;    // per drawer open / cart change, so it can never loop
    var MAX_BUSY_WAITS = 6;

    var timer = null;
    var seq = 0;
    var repairs = 0;
    var busyWaits = 0;
    var selfEvent = false;

    function enabled() {
      return !(document.body && document.body.getAttribute('data-wt-cart-guard') === 'false');
    }
    function getDrawer() { return document.querySelector('[data-drawer="drawer-cart"]'); }
    function isOpen(drawer) { return !!drawer && drawer.classList.contains('drawer--visible'); }

    function rendered(drawer) {
      var rows = drawer.querySelectorAll('[data-line-items] [data-cart-item]');
      var qty = {};
      rows.forEach(function (row) {
        var input = row.querySelector('[data-update-cart]');
        if (input) qty[input.getAttribute('data-update-cart')] = parseInt(input.value, 10);
      });
      var emptyEl = drawer.querySelector('[data-cart-empty]');
      return {
        rows: rows.length,
        qty: qty,
        hasEmptyEl: !!emptyEl,
        emptyShown: !!emptyEl && !emptyEl.classList.contains('cart--hidden')
      };
    }

    function inSync(cart, r) {
      var items = cart.items || [];
      if (!cart.item_count) return r.hasEmptyEl ? r.emptyShown : true;
      if (r.emptyShown) return false;
      if (r.rows !== items.length) return false;
      for (var i = 0; i < items.length; i++) {
        if (r.qty[items[i].key] !== items[i].quantity) return false;
      }
      return true;
    }

    function schedule(ms) {
      clearTimeout(timer);
      seq++;
      timer = setTimeout(check, ms);
    }

    function repair(cart) {
      repairs++;
      selfEvent = true;
      try {
        // change = gives Pipeline the real cart and marks its drawer stale; reload = re-render now
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart, wtGuard: true }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      } finally {
        selfEvent = false;
      }
    }

    function check() {
      var drawer = getDrawer();
      if (!enabled() || !isOpen(drawer)) return;

      // Pipeline is mid-update (quantity change / remove): wait for it rather than fight it
      var loader = drawer.querySelector('[data-cart-loading]');
      if (loader && loader.classList.contains('cart--loading') && busyWaits < MAX_BUSY_WAITS) {
        busyWaits++;
        schedule(BUSY_MS);
        return;
      }

      var mySeq = seq;
      fetch(cartRoute + '.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (mySeq !== seq) return;            // something newer happened, that check wins
          var d = getDrawer();
          if (!isOpen(d)) return;
          var r = rendered(d);
          if (inSync(cart, r)) return;

          var info = { cartItems: (cart.items || []).length, shownRows: r.rows, emptyShown: r.emptyShown, repairs: repairs };
          if (repairs >= MAX_REPAIRS) {
            if (window.console) console.warn('[WT] cart drawer still out of sync after repair', info);
            document.dispatchEvent(new CustomEvent('wt:cart:desync', { detail: info }));
            return;
          }
          if (window.console) console.warn('[WT] cart drawer out of sync, re-rendering', info);
          document.dispatchEvent(new CustomEvent('wt:cart:repair', { detail: info }));
          repair(cart);
          schedule(VERIFY_MS);
        })
        .catch(function () {});
    }

    function reset() { repairs = 0; busyWaits = 0; }

    function bind() {
      var drawer = getDrawer();
      if (!drawer || drawer.__wtGuardBound) return;
      drawer.__wtGuardBound = true;
      // theme:drawer:open does not bubble, so it has to be bound on the drawer itself
      drawer.addEventListener('theme:drawer:open', function () { reset(); schedule(SETTLE_MS); });
      drawer.addEventListener('theme:drawer:close', function () { clearTimeout(timer); seq++; });
    }

    document.addEventListener('theme:cart:change', function () {
      if (selfEvent) return;
      bind();
      if (isOpen(getDrawer())) { reset(); schedule(SETTLE_MS); }
    });

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind); else bind();
    // The theme editor re-renders sections, which replaces the drawer element
    document.addEventListener('shopify:section:load', function () { setTimeout(bind, 0); });

    WT.cartGuard = { check: function () { reset(); schedule(0); } };
  })();

  /* ------------------------------------------------------------------ */
  /* Klaviyo auto-open guard                                             */
  /* ------------------------------------------------------------------ */
  var GUARD_WINDOW = 6000; // ms after an add-to-cart during which auto-opens are reverted
  var lastAdd = 0;

  function armGuard() { lastAdd = Date.now(); }

  // Pipeline fires this on its own adds; our helper fires wt:cart:adding.
  document.addEventListener('theme:cart:popdown', armGuard);
  document.addEventListener('wt:cart:adding', armGuard);
  // Also arm when any real product-form add button is clicked
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-add-to-cart], [name="add"], [data-wt-atc]')) armGuard();
  }, true);

  function isClosing(el) {
    var st = el.getAttribute('style') || '';
    return st.indexOf('klaviyo-fadeout') !== -1 || el.style.display === 'none';
  }

  function isKlaviyoOverlay(el) {
    if (!(el instanceof Element)) return false;
    if (isClosing(el)) return false;
    var sig = (el.id + ' ' + el.className + ' ' + (el.getAttribute('data-testid') || '') + ' ' + el.tagName).toLowerCase();
    if (sig.indexOf('klaviyo') === -1 && sig.indexOf('kl-') === -1) return false;
    // Ignore the small launcher/bubble; only care about panels, dialogs and popups
    if (/launcher|bubble|badge|teaser/.test(sig)) return false;
    var role = el.getAttribute('role') || '';
    if (role === 'dialog' || el.getAttribute('aria-modal') === 'true') return true;
    if (/hub|popup|modal|dialog|flyout|drawer|overlay|form-/.test(sig)) return true;
    var cs = window.getComputedStyle(el);
    return cs.position === 'fixed' && el.getBoundingClientRect().width > 200;
  }

  function dismiss(el) {
    if (!document.body.contains(el) || isClosing(el)) return;
    var closeBtn = el.querySelector('button[aria-label*="lose" i], [data-testid*="close" i], .klaviyo-close-form, button[class*="close" i]');
    if (closeBtn) {
      closeBtn.click();
      return;
    }
    // Customer Hub exposes a global toggle in some versions
    try {
      if (window.klaviyo && typeof window.klaviyo.closeHub === 'function') { window.klaviyo.closeHub(); return; }
    } catch (err) {}
    el.style.setProperty('display', 'none', 'important');
  }

  function guardEnabled() {
    return document.body && document.body.getAttribute('data-wt-chat-guard') !== 'false';
  }

  function scan(nodes) {
    if (!guardEnabled()) return;
    if (Date.now() - lastAdd > GUARD_WINDOW) return;
    nodes.forEach(function (node) {
      if (!(node instanceof Element)) return;
      var candidates = [node];
      node.querySelectorAll && node.querySelectorAll('[class*="klaviyo" i],[id*="klaviyo" i],[data-testid*="klaviyo" i],[class*="kl-" i]').forEach(function (c) { candidates.push(c); });
      candidates.forEach(function (c) {
        if (isKlaviyoOverlay(c)) {
          // Give Klaviyo a tick to finish mounting, then close it
          setTimeout(function () { dismiss(c); }, 60);
        }
      });
    });
  }

  function startObserver() {
    if (!window.MutationObserver || !document.body) return;
    var obs = new MutationObserver(function (mutations) {
      if (Date.now() - lastAdd > GUARD_WINDOW) return;
      var added = [];
      mutations.forEach(function (m) {
        if (m.type === 'childList') { m.addedNodes.forEach(function (n) { added.push(n); }); }
        else if (m.type === 'attributes' && m.target instanceof Element) { added.push(m.target); }
      });
      if (added.length) scan(added);
    });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'open'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver);
  } else {
    startObserver();
  }

  /* ------------------------------------------------------------------ */
  /* Klaviyo ghost-popup fix (never let a closed popup block taps)       */
  /* ------------------------------------------------------------------ */
  (function () {
    var FADE_MS = 700; // Klaviyo's fade-out is 350ms; give it double before forcing it away

    try {
      var css = document.createElement('style');
      css.id = 'wt-klaviyo-ghost-fix';
      css.textContent =
        '[role="dialog"][style*="klaviyo-fadeout"],[role="dialog"][style*="klaviyo-fadeout"] *{pointer-events:none!important}' +
        'html.wt-cart-open [class*="kl-teaser"]{display:none!important}' +
        /* Keep the cart drawer pinned to the real bottom of the screen on phones: dvh follows
           the browser toolbar as it shows/hides, and the bottom block clears the home bar. */
        '@supports (height:100dvh){.cart__drawer .drawer__content{height:100dvh!important;max-height:100dvh!important}}' +
        '.cart__drawer .drawer__bottom{padding-bottom:env(safe-area-inset-bottom,0px)}';
      (document.head || document.documentElement).appendChild(css);
    } catch (err) {}

    function isKlaviyoDialog(el) {
      return el instanceof Element && el.getAttribute('role') === 'dialog' &&
        /kl-private|needsclick|klaviyo/i.test(String(el.className) + ' ' + (el.id || ''));
    }

    function handle(el) {
      if (!isKlaviyoDialog(el)) return;
      var st = el.getAttribute('style') || '';
      if (st.indexOf('klaviyo-fadeout') === -1) { el.__wtFadeAt = 0; return; }
      if (el.__wtFadeAt) return;
      el.__wtFadeAt = Date.now();
      setTimeout(function () {
        var now = el.getAttribute('style') || '';
        // Still mounted and still in its closing state: take it out of the page
        if (document.body.contains(el) && now.indexOf('klaviyo-fadeout') !== -1) {
          el.style.setProperty('display', 'none', 'important');
        }
      }, FADE_MS);
    }

    /* Klaviyo Customer Hub / Chat: Klaviyo opens it by itself right after an add-to-cart.
       It slides a sheet up over the cart, dims the page with a full-screen veil and locks
       body scrolling, which knocks the cart drawer's Check Out area out of place. Close it
       properly (its own close button, so Klaviyo also releases the scroll lock) whenever it
       opens while the cart is open or within 10s of an add-to-cart. */
    var HUB_WINDOW = 10000;
    var hubClosing = false;
    function cartIsOpen() {
      var d = document.querySelector('[data-drawer="drawer-cart"]');
      return !!d && d.classList.contains('drawer--visible');
    }
    function recentAdd() {
      var last = Math.max(lastAdd || 0, WT.lastAddAt || 0);
      return Date.now() - last < HUB_WINDOW;
    }
    function hubOpen() {
      return !!document.querySelector('.kl-hub-modal-veil--open, .kl-hub-modal-veil--opened');
    }
    function closeHub() {
      if (hubClosing || !hubOpen()) return;
      if (!(cartIsOpen() || recentAdd())) return;
      if (document.body && document.body.getAttribute('data-wt-chat-guard') === 'false') return;
      var btn = document.querySelector('.kl-hub-drawer button[aria-label*="lose" i], .atlas-drawer button[aria-label*="lose" i], .kl-hub-drawer__header button');
      if (!btn) return;
      hubClosing = true;
      btn.click();
      setTimeout(function () { hubClosing = false; }, 400);
    }

    function sweep() {
      document.querySelectorAll('[role="dialog"]').forEach(handle);
      closeHub();
    }

    function syncCartOpen() {
      var d = document.querySelector('[data-drawer="drawer-cart"]');
      document.documentElement.classList.toggle('wt-cart-open', !!d && d.classList.contains('drawer--visible'));
    }

    function start() {
      if (!window.MutationObserver || !document.body) return;
      new MutationObserver(function (mutations) {
        var cartTouched = false;
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type === 'attributes') {
            if (m.attributeName === 'style') handle(m.target);
            if (m.attributeName === 'class' && /kl-hub/.test(String(m.target.className))) setTimeout(closeHub, 50);
            else if (m.target.getAttribute && m.target.getAttribute('data-drawer') === 'drawer-cart') cartTouched = true;
          } else if (m.addedNodes.length) {
            cartTouched = true;
          }
        }
        if (cartTouched) { syncCartOpen(); setTimeout(closeHub, 50); }
      }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class'] });
      sweep();
      syncCartOpen();
      // Safety net for popups that were already stuck before this script ran
      setInterval(sweep, 2000);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  })();

  /* ------------------------------------------------------------------ */
  /* Tap rescue: Check Out / Add to Cart must always respond             */
  /* ------------------------------------------------------------------ */
  (function () {
    var CHECKOUT_SEL = 'button[name="checkout"], input[name="checkout"], .checkout__button';
    var ATC_SEL = '[data-add-to-cart], button[name="add"]';
    var FALLBACK_MS = 3500;

    function enabled() {
      return !(document.body && document.body.getAttribute('data-wt-tap-rescue') === 'false');
    }

    function isShown(el) {
      if (!el || !el.getBoundingClientRect) return false;
      var r = el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return false;
      var cs = window.getComputedStyle(el);
      return cs.visibility !== 'hidden' && cs.display !== 'none';
    }

    // Is this element (and what it paints) effectively invisible to the shopper?
    function isInvisible(el) {
      var op = 1;
      for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
        var cs = window.getComputedStyle(n);
        if (cs.visibility === 'hidden' || cs.display === 'none') return true;
        op *= parseFloat(cs.opacity);
        if (op < 0.05) return true;
      }
      return false;
    }

    function inside(r, x, y) { return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom; }

    // Topmost visible Check Out / Add to Cart button whose box contains the tap point
    function buttonAt(x, y) {
      var list = document.querySelectorAll(CHECKOUT_SEL + ',' + ATC_SEL);
      for (var i = 0; i < list.length; i++) {
        var b = list[i];
        if (b.disabled || !isShown(b)) continue;
        if (inside(b.getBoundingClientRect(), x, y)) return b;
      }
      return null;
    }

    function goCheckout() {
      window.location.href = (root || '') + '/checkout';
    }

    var fallbackTimer = null;
    function armCheckoutFallback() {
      clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(function () {
        if (document.visibilityState === 'hidden') return; // already navigating / app switched
        if (window.console) console.warn('[WT] checkout submit did not navigate, sending to /checkout');
        document.dispatchEvent(new CustomEvent('wt:checkout:fallback'));
        goCheckout();
      }, FALLBACK_MS);
    }
    window.addEventListener('pagehide', function () { clearTimeout(fallbackTimer); });
    window.addEventListener('beforeunload', function () { clearTimeout(fallbackTimer); });

    // a) invisible layer caught a tap meant for the button
    document.addEventListener('click', function (e) {
      if (!enabled() || !e.isTrusted) return;
      var t = e.target;
      if (!(t instanceof Element)) return;
      var x = e.clientX, y = e.clientY;
      if (!x && !y) return; // keyboard-triggered click

      var own = t.closest(CHECKOUT_SEL + ',' + ATC_SEL);
      if (own) {
        // The tap reached the real button: just make sure Check Out can't silently stall
        if (own.matches(CHECKOUT_SEL)) armCheckoutFallback();
        return;
      }

      var btn = buttonAt(x, y);
      if (!btn) return;
      if (!isInvisible(t)) return; // a visible popup/panel is on top: respect it

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      if (window.console) console.warn('[WT] tap caught by invisible layer, passing it to', btn);
      try { t.style.setProperty('pointer-events', 'none', 'important'); } catch (err) {}

      if (btn.matches(CHECKOUT_SEL)) {
        armCheckoutFallback();
        var form = btn.form || btn.closest('form');
        // requestSubmit keeps name="checkout" in the post; without it go straight to /checkout
        if (form && typeof form.requestSubmit === 'function') form.requestSubmit(btn);
        else goCheckout();
      } else {
        btn.click();
      }
    }, true);
  })();
})();